import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Loader2, Sparkles, SlidersHorizontal, Music4, CalendarClock, Gauge, Layers } from "lucide-react";

import Header from "@/components/Header";
import UploadZone from "@/components/UploadZone";
import PrivacyAlert from "@/components/PrivacyAlert";
import { api, getGenres, startAnalyze, getJob, getUsage, getMyCreditRequest } from "@/lib/api";
import { HOME } from "@/constants/testIds";
import ConsentModal from "@/components/ConsentModal";
import AccessBlockedScreen from "@/components/AccessBlockedScreen";
import CreditRequestModal from "@/components/CreditRequestModal";
import LoginPrompt from "@/components/LoginPrompt";
import GenreSelector from "@/components/GenreSelector";
import { useElapsedSeconds, fmtElapsed } from "@/lib/useElapsed";
import { playBeep, playUploadDone } from "@/lib/beep";
import { useAuth } from "@/context/AuthContext";

const LAST_GENRE_KEY = "spectraloom_last_genre";
const LAST_VISIT_KEY = "spectraloom_last_visit_day";

const readLastGenre = () => {
  try {
    return localStorage.getItem(LAST_GENRE_KEY) || "";
  } catch {
    return "";
  }
};
const saveLastGenre = (g) => {
  try {
    if (g) localStorage.setItem(LAST_GENRE_KEY, g);
  } catch {
    /* ignore */
  }
};
const todayKey = () => new Date().toISOString().slice(0, 10);
const readLastVisitDay = () => {
  try {
    return localStorage.getItem(LAST_VISIT_KEY) || "";
  } catch {
    return "";
  }
};
const saveLastVisitDay = (d) => {
  try {
    localStorage.setItem(LAST_VISIT_KEY, d);
  } catch {
    /* ignore */
  }
};

export default function Home() {
  const nav = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const STAGE_LABELS = {
    upload: t("home.stage_upload"),
    pending: t("home.stage_pending"),
    queued: t("home.stage_queued"),
    processing: t("home.stage_processing"),
    done: t("home.stage_done"),
  };
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("mastering");
  const [genre, setGenreState] = useState(readLastGenre);
  const setGenre = (g) => {
    setGenreState(g);
    saveLastGenre(g);
  };
  const [taxonomy, setTaxonomy] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [stage, setStage] = useState("upload");
  const [jobPct, setJobPct] = useState(0);
  const [queuePos, setQueuePos] = useState(null);
  const [usage, setUsage] = useState(null);
  const [creditRequestPending, setCreditRequestPending] = useState(null);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const pollRef = useRef(null);
  const analyzeElapsed = useElapsedSeconds(loading);

  const refreshUsage = () =>
    getUsage()
      .then(setUsage)
      .catch(() => {});

  const refreshMyRequest = () =>
    getMyCreditRequest()
      .then((r) => setCreditRequestPending(r.pending || null))
      .catch(() => {});

  useEffect(() => {
    getGenres()
      .then((d) => {
        setTaxonomy(d.taxonomy || []);
        setProfiles(d.profiles || {});
        // Initialise/reset genre to the first micro-genre of the taxonomy
        // whenever `genre` is empty OR points to a legacy value not in the
        // new taxonomy tree (we still validate legacy via profiles for history).
        const isInTaxonomy = (g) =>
          (d.taxonomy || []).some((macro) =>
            macro.subcategories.some((sub) =>
              sub.microgenres.some((m) => m.id === g)
            )
          );
        if ((!genre || !isInTaxonomy(genre)) && d.taxonomy?.length) {
          const first = d.taxonomy[0]?.subcategories?.[0]?.microgenres?.[0]?.id;
          if (first) setGenre(first);
        }

        // "Welcome back" toast · shown once per day if the user already has a
        // saved genre that still exists in the taxonomy. Uses a light Sonner
        // toast with two clear actions.
        const savedGenre = readLastGenre();
        const today = todayKey();
        if (
          savedGenre &&
          isInTaxonomy(savedGenre) &&
          readLastVisitDay() !== today
        ) {
          saveLastVisitDay(today);
          // Resolve the localized label for the saved genre
          let label = savedGenre;
          for (const macro of d.taxonomy || []) {
            for (const sub of macro.subcategories) {
              const m = sub.microgenres.find((x) => x.id === savedGenre);
              if (m) {
                label = m.label;
                break;
              }
            }
          }
          setTimeout(() => {
            toast(t("home.welcome_back_title"), {
              description: t("home.welcome_back_body", { genre: label }),
              duration: 8000,
              action: {
                label: t("home.welcome_back_change"),
                onClick: () => {
                  const first =
                    d.taxonomy[0]?.subcategories?.[0]?.microgenres?.[0]?.id;
                  if (first) setGenre(first);
                },
              },
            });
          }, 800);
        }
      })
      .catch(() => toast.error(t("home.toast_genres_fail")));
    refreshUsage();
    if (user && !user.is_admin) refreshMyRequest();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [i18n.language, t, user?.user_id]);

  const onFileChange = (f) => {
    setFile(f);
  };

  // Consent modal state:
  // - null = no modal
  // - { firstTime: bool } = show modal, remember whether ToS/Privacy were already accepted
  const [consentPrompt, setConsentPrompt] = useState(null);
  // Login prompt for unauthenticated users trying to trigger an analysis
  const [showLogin, setShowLogin] = useState(false);

  const onAnalyze = async () => {
    if (!file) {
      toast.error(t("home.toast_select_first"));
      return;
    }
    // Auth gate · login required before spending an analysis
    if (!user) {
      setShowLogin(true);
      return;
    }
    if (usage?.reached) {
      toast.error(t("home.toast_quota_exceeded", { limit: usage.limit }));
      return;
    }
    // Consent gate: check backend for stored ToS/Privacy consent for this user.
    // - No consent yet → modal with all 3 checkboxes (Terms + Privacy + Copyright)
    // - Consent exists → SHORT modal with only Copyright confirmation
    //
    // We deliberately DO NOT use the "quick" 1-click re-confirmation flow
    // anymore: the product requirement is that EVERY upload after the
    // first must show the shorter copyright banner. Admins bypass consent
    // entirely (their token skips the backend consent check).
    let firstTime = true;
    try {
      const r = await api.get("/consent");
      firstTime = !r.data.has_valid_consent;
    } catch {
      /* fail-open · modal will act as first-time */
    }
    setConsentPrompt({ firstTime, quick: false });
  };

  // Called after the user accepts the consent modal · performs the actual upload.
  const startUploadAfterConsent = async () => {
    setConsentPrompt(null);
    setLoading(true);
    setUploadPct(0);
    setJobPct(0);
    setStage("upload");
    try {
      const res = await startAnalyze(file, genre, mode, (e) => {
        if (e.total) setUploadPct(Math.round((e.loaded / e.total) * 100));
      });
      // Distinct upload-complete chime BEFORE the analysis one · user
      // hears an audible "file received" cue while the DSP job is queued.
      playUploadDone();
      setStage("pending");
      refreshUsage(); // refresh badge

      const { job_id, analysis_id } = res;
      // Tolerate transient network / 502 blips: only give up after
      // MAX_POLL_FAILS consecutive failures. This is critical on production
      // (Cloudflare + K8s ingress occasionally 502s for a few seconds).
      // MOBILE FIX (Feb 2026): also handle Android/Chrome background tab
      // throttling by (a) re-checking immediately on visibilitychange and
      // (b) falling back to navigating to /analysis/{id} directly after a
      // hard timeout · the Analysis page itself polls & handles pending states.
      let failCount = 0;
      const MAX_POLL_FAILS = 5;
      const startedAt = Date.now();
      const HARD_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes · safety net for stuck polling on mobile

      const cleanupHandlers = () => {
        if (pollRef.current) clearInterval(pollRef.current);
        document.removeEventListener("visibilitychange", onVisibility);
      };

      const finish = (job) => {
        cleanupHandlers();
        setStage("done");
        playBeep();
        toast.success(t("home.toast_analysis_done"));
        nav(`/analysis/${analysis_id}`);
      };

      const checkJob = async () => {
        try {
          const job = await getJob(job_id);
          failCount = 0;
          setJobPct(job.progress || 0);
          if (job.status === "queued" || job.status === "pending") {
            setStage("queued");
            const pos = Number(job.queue_position ?? 0);
            setQueuePos(pos);
          } else {
            setQueuePos(null);
          }
          if (job.status === "processing") setStage("processing");
          if (job.status === "done") {
            finish(job);
          } else if (job.status === "failed") {
            cleanupHandlers();
            setLoading(false);
            toast.error(job.error || t("home.toast_analysis_error"));
          }
        } catch (err) {
          failCount += 1;
          console.warn(`Poll failed (${failCount}/${MAX_POLL_FAILS})`, err?.message);
          if (failCount >= MAX_POLL_FAILS) {
            cleanupHandlers();
            // MOBILE UX: don't lose the analysis · jump straight to the
            // Analysis page. It will show the pending/processing UI or the
            // finished result depending on backend state. Prevents the "I
            // paid a credit and see nothing" scenario on Android.
            setStage("done");
            toast(t("home.toast_analysis_recovering"));
            nav(`/analysis/${analysis_id}`);
          }
        }
        // Hard timeout: navigate to Analysis page anyway (safe: page handles
        // both done and still-processing states).
        if (Date.now() - startedAt > HARD_TIMEOUT_MS) {
          cleanupHandlers();
          setStage("done");
          nav(`/analysis/${analysis_id}`);
        }
      };

      // Refresh once immediately when tab becomes visible again · solves the
      // Chrome/Firefox mobile background throttling issue.
      const onVisibility = () => {
        if (document.visibilityState === "visible") {
          checkJob();
        }
      };
      document.addEventListener("visibilitychange", onVisibility);

      pollRef.current = setInterval(checkJob, 2000);
      // Also call once immediately (no need to wait 2s for first tick).
      checkJob();
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.detail || err.message;
      if (status === 429) {
        toast.error(msg);
        refreshUsage();
      } else {
        toast.error(t("home.toast_analyze_fail", { msg }));
      }
      setLoading(false);
    }
  };

  const profile = profiles[genre];

  // Live counts of the whole taxonomy, shown as a small stat in the genre card
  const taxonomyStats = useMemo(() => {
    let subs = 0;
    let micros = 0;
    for (const macro of taxonomy) {
      subs += macro.subcategories.length;
      for (const sub of macro.subcategories) {
        micros += sub.microgenres.length;
      }
    }
    return { macros: taxonomy.length, subs, micros };
  }, [taxonomy]);

  // Genre card is rendered twice: on mobile it appears BEFORE the upload
  // block (inside the left column, lg:hidden), on desktop it stays in the
  // right aside as originally designed (hidden lg:block).
  const genreCard = (
    <div className="card p-6">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          <Music4 size={16} className="text-[color:var(--brand)]" />
          <div className="tiny-caps">{t("home.genre_card_title")}</div>
        </div>
        <div
          className="flex items-center gap-1.5 text-[10px] tiny-caps text-[color:var(--fg-secondary)] px-2.5 py-1 rounded-full border border-white/10 bg-white/[0.03]"
          data-testid="genre-count-badge"
          title={t("home.genre_count_hint")}
        >
          <Layers size={11} className="text-[color:var(--brand)]" />
          {t("home.genre_count", {
            subs: taxonomyStats.subs,
            micros: taxonomyStats.micros,
          })}
        </div>
      </div>
      <p className="text-sm text-[color:var(--fg-secondary)] mb-4">
        {t("home.genre_card_hint")}
      </p>
      <div data-testid={HOME.genreSelect}>
        <GenreSelector
          taxonomy={taxonomy}
          microValue={genre}
          onMicroChange={setGenre}
        />
      </div>

      {profile && (
        <div className="mt-5 pt-5 border-t border-white/5 grid grid-cols-2 gap-4">
          <Stat label={t("home.lufs_target")} value={`${mode === "mix" ? profile.lufs_mix : profile.lufs_master} dB`} />
          <Stat label={t("home.bpm_range")} value={`${profile.bpm[0]}–${profile.bpm[1]}`} />
          <div className="col-span-2">
            <div className="tiny-caps mb-1">{t("home.engineer_note")}</div>
            <div className="text-sm text-[color:var(--fg-secondary)]">{profile.notes_it}</div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen">
      {/* Block only users with ZERO credits. Free-Trial users (limit=1)
          proceed straight to the app · the auto-approve flow gives every
          new signup exactly 1 mastering + 2 previews. When they exhaust
          it, `/api/analyze` returns a friendly 429 with the quota_reached
          message and a link to contact the admin for more credits. */}
      {user && !user.is_admin && usage && usage.limit === 0 ? (
        <AccessBlockedScreen />
      ) : (
      <>
      <Header />
      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 relative z-10">
        <PrivacyAlert />
        <section className="grid lg:grid-cols-[1.15fr_1fr] gap-10 items-start">
          {/* Left: hero + upload */}
          <div className="fade-up">
            <div className="tiny-caps text-[color:var(--brand)] mb-4">
              {t("home.kicker")}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-white mb-5">
              {t("home.title_prefix")}{" "}
              <span className="text-[color:var(--brand)]">{t("home.title_highlight")}</span>{" "}
              {t("home.title_suffix")}
              <br /> {t("home.title_suffix2")}
            </h1>
            <p className="text-[color:var(--fg-secondary)] text-base sm:text-lg leading-relaxed max-w-xl mb-8">
              {t("home.subtitle")}
            </p>

            <div className="flex gap-2 mb-6">
              <button
                data-testid={HOME.modeMastering}
                className={`pill ${mode === "mastering" ? "active" : ""}`}
                onClick={() => setMode("mastering")}
              >
                {t("home.mode_mastering")}
              </button>
              <button
                data-testid={HOME.modeMix}
                className={`pill ${mode === "mix" ? "active" : ""}`}
                onClick={() => setMode("mix")}
              >
                {t("home.mode_mix")}
              </button>
            </div>

            {/* Mobile-only: genre card ABOVE the upload zone. Hidden on lg+ */}
            <div className="lg:hidden mb-6" data-testid="genre-card-mobile">
              {genreCard}
            </div>

            <UploadZone
              file={file}
              onFile={onFileChange}
              disabled={loading || usage?.reached}
              // Auth gate BEFORE opening the file picker · guest uploads
              // are not allowed anywhere in the app. Returning `true`
              // tells UploadZone to skip its native open action.
              onBlocked={() => {
                if (!user) {
                  setShowLogin(true);
                  return true;
                }
                return false;
              }}
            />

            {usage?.reached ? (
              <div
                className="mt-6 card-elev border-l-4 border-l-[color:var(--meter-poor)] p-5 flex items-start gap-3"
                data-testid="quota-reached-card"
              >
                <div className="w-10 h-10 rounded-full bg-red-500/15 flex items-center justify-center shrink-0">
                  <CalendarClock size={18} className="text-[color:var(--meter-poor)]" />
                </div>
                <div className="flex-1">
                  {creditRequestPending ? (
                    <>
                      <div className="text-white font-semibold mb-1" data-testid="credit-pending-title">
                        {t("credits.pending_title")}
                      </div>
                      <p className="text-sm text-[color:var(--fg-secondary)]">
                        {t("credits.pending_body", {
                          amount: creditRequestPending.requested_amount,
                          date: new Date(creditRequestPending.created_at).toLocaleDateString(
                            i18n.language === "en" ? "en-US" : "it-IT",
                            { day: "numeric", month: "short", year: "numeric" }
                          ),
                        })}
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="text-white font-semibold mb-1">
                        {t("home.quota_title", { limit: usage.limit })}
                      </div>
                      <p className="text-sm text-[color:var(--fg-secondary)] mb-3">
                        {t("home.quota_body")}
                      </p>
                      <button
                        onClick={() => setShowCreditModal(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 text-xs border border-amber-500/30"
                        data-testid="request-credits-btn"
                      >
                        <Sparkles size={12} /> {t("credits.request_more")}
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : null}

            <div className="mt-6 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
              <button
                className="btn-primary flex items-center justify-center gap-2 flex-1"
                data-testid={HOME.analyzeBtn}
                disabled={!file || loading || usage?.reached}
                onClick={onAnalyze}
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    <span>
                      {stage === "queued" && queuePos > 0
                        ? t("home.stage_queued_pos", { pos: queuePos })
                        : STAGE_LABELS[stage] || t("home.processing")}
                      {stage === "queued" && queuePos > 0
                        ? ""
                        : `… ${stage === "upload" ? `${uploadPct}%` : `${jobPct}%`}`}
                    </span>
                    {analyzeElapsed > 0 && (
                      <span
                        className="text-[10px] opacity-70 metric-value"
                        data-testid="analyze-elapsed"
                      >
                        {fmtElapsed(analyzeElapsed)}
                      </span>
                    )}
                  </>
                ) : usage?.reached ? (
                  <>
                    <CalendarClock size={18} />
                    <span>{t("home.limit_reached_btn")}</span>
                  </>
                ) : (
                  <>
                    <Sparkles size={18} />
                    <span>{t("home.analyze_btn")}</span>
                  </>
                )}
              </button>
              <div className="flex flex-col items-start sm:items-end gap-1">
                <div className="text-xs text-[color:var(--fg-muted)] metric-value">
                  {t("home.upload_hint")}
                </div>
                {usage && (
                  <div
                    className="flex items-center gap-1.5 text-[11px]"
                    data-testid="usage-badge"
                  >
                    <Gauge
                      size={12}
                      className={
                        usage.admin
                          ? "text-[color:var(--meter-excellent)]"
                          : usage.reached
                            ? "text-[color:var(--meter-poor)]"
                            : usage.count >= usage.limit - 2
                              ? "text-[color:var(--meter-good)]"
                              : "text-[color:var(--meter-excellent)]"
                      }
                    />
                    <span
                      className={`metric-value ${
                        usage.admin
                          ? "text-[color:var(--meter-excellent)]"
                          : usage.reached
                            ? "text-[color:var(--meter-poor)]"
                            : "text-[color:var(--fg-secondary)]"
                      }`}
                    >
                      {usage.admin
                        ? t("home.quota_admin")
                        : t("home.quota_badge", { count: usage.count, limit: usage.limit })}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right: genre selector + info (desktop only · mobile shows it inline above upload) */}
          <aside className="flex flex-col gap-6 fade-up" style={{ animationDelay: "80ms" }}>
            <div className="hidden lg:block">{genreCard}</div>

            <div className="card p-6 flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <SlidersHorizontal size={16} className="text-[color:var(--meter-hit)]" />
                <div className="tiny-caps">{t("home.receive_title")}</div>
              </div>
              <ul className="space-y-2 text-sm text-[color:var(--fg-secondary)]">
                {t("home.receive_items", { returnObjects: true }).map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[color:var(--brand)] shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </section>
      </main>
      {consentPrompt && (
        <ConsentModal
          firstTime={consentPrompt.firstTime}
          quick={consentPrompt.quick}
          onAccepted={startUploadAfterConsent}
          onClose={() => setConsentPrompt(null)}
        />
      )}
      {showLogin && <LoginPrompt onClose={() => setShowLogin(false)} />}
      {showCreditModal && (
        <CreditRequestModal
          onClose={() => setShowCreditModal(false)}
          onSubmitted={refreshMyRequest}
        />
      )}
      </>
      )}
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <div className="tiny-caps mb-1">{label}</div>
    <div className="metric-value text-white text-lg">{value}</div>
  </div>
);

function nextMonthLabel(monthKey, lang = "it", t = null) {
  if (!monthKey) return t ? t("home.next_month_fallback") : "prossimo mese";
  const [y, m] = monthKey.split("-").map(Number);
  const d = new Date(Date.UTC(y, m, 1));
  const locale = lang === "en" ? "en-US" : "it-IT";
  return d.toLocaleDateString(locale, { month: "long", year: "numeric" });
}